import { useState } from 'react';
import { Bug, Loader2 } from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

type FeedbackType = 'erro' | 'sugestao';

const PLACEHOLDER: Record<FeedbackType, string> = {
  erro: 'Descreve o que tu tentou fazer e o que deu errado...',
  sugestao: 'Conta tua ideia ou o que poderia melhorar...',
};

async function captureViewport(): Promise<string | null> {
  try {
    return await htmlToImage.toPng(document.body, {
      width: window.innerWidth,
      height: window.innerHeight,
      style: {
        transform: `translate(-${window.scrollX}px, -${window.scrollY}px)`,
        transformOrigin: 'top left',
      },
      cacheBust: true,
      pixelRatio: 1,
      filter: (node) => {
        if (!(node instanceof Element)) return true;
        if (node.matches?.('[data-feedback-trigger="true"]')) return false;
        return true;
      },
    });
  } catch (err) {
    console.error('[feedback] captura falhou', err);
    return null;
  }
}

function gatherContext() {
  return {
    pathname: window.location.pathname,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: screen.width, height: screen.height },
    pixel_ratio: window.devicePixelRatio,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || undefined,
  };
}

export const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [type, setType] = useState<FeedbackType>('erro');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleOpen = async () => {
    if (capturing || open) return;
    setCapturing(true);
    const shot = await captureViewport();
    setScreenshot(shot);
    setCapturing(false);
    setOpen(true);
  };

  const handleOpenChange = (next: boolean) => {
    if (submitting) return;
    setOpen(next);
    if (!next) {
      setTimeout(() => {
        setMessage('');
        setType('erro');
        setScreenshot(null);
      }, 200);
    }
  };

  const handleSubmit = async () => {
    if (!message.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('submit-feedback', {
        body: {
          type,
          message: message.trim(),
          screenshot_base64: screenshot ?? undefined,
          url: window.location.href,
          context: gatherContext(),
        },
      });
      if (error) throw error;
      toast({
        title: 'Feedback enviado',
        description: 'Obrigado! Seu feedback já está com o time de suporte.',
      });
      setOpen(false);
      setTimeout(() => {
        setMessage('');
        setType('erro');
        setScreenshot(null);
      }, 200);
    } catch (err) {
      console.error('[feedback] envio falhou', err);
      toast({
        title: 'Não foi possível enviar',
        description: err instanceof Error ? err.message : 'Tenta de novo daqui a pouco.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-feedback-trigger="true"
        onClick={handleOpen}
        disabled={capturing}
        className="hidden md:flex items-center gap-1.5 h-8 px-3 rounded-md border border-color bg-secondary/50 text-xs text-tertiary hover:bg-hover transition-colors disabled:opacity-60"
        title="Reportar erro ou sugestão"
      >
        {capturing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Bug className="w-3.5 h-3.5" />
        )}
        <span>Feedback</span>
      </button>
      <button
        type="button"
        data-feedback-trigger="true"
        onClick={handleOpen}
        disabled={capturing}
        className="md:hidden flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover transition-colors disabled:opacity-60"
        title="Reportar erro ou sugestão"
      >
        {capturing ? (
          <Loader2 className="w-4 h-4 animate-spin text-secondary" />
        ) : (
          <Bug className="w-4 h-4 text-secondary" />
        )}
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Reportar erro ou sugestão
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm mb-2 block">Tipo</Label>
              <RadioGroup
                value={type}
                onValueChange={(v) => setType(v as FeedbackType)}
                className="flex gap-6"
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="erro" id="feedback-erro" />
                  <span className="text-sm">Erro</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="sugestao" id="feedback-sugestao" />
                  <span className="text-sm">Sugestão</span>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="feedback-message" className="text-sm mb-2 block">
                O que aconteceu
              </Label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={PLACEHOLDER[type]}
                rows={5}
                maxLength={5000}
                disabled={submitting}
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
